// RenderPipeline chain (WebGPU + TSL):
//   scene → [ssao composite] → [taau|traa] → [motion blur] → [dof]
//     → (bloom + anamorphic) → colorGrade → renderOutput → [fxaa] → lut3D.
// renderOutput applies tone mapping + sRGB so FXAA + LUT run on
// perceptual sRGB color. `outputColorTransform=false` prevents double
// conversion. Anamorphic runs in HDR linear space alongside bloom so
// only genuinely bright highlights flare (threshold is luminance-based).
//
// AA runs in one of four modes per `CONFIG.render.aaMode`:
//   'fxaa' — screen-space AA after tone map. Quality driven by three
//            uniforms (contrast/relative/subpixel) so the quality preset
//            is fully live-tweakable without rebuilding the pipeline.
//   'taau' — temporal AA with upscaling at the head of the chain.
//            Scene renders at `aaTaauResolutionScale` of the output;
//            TAAU resolves to full output size. Velocity MRT required.
//   'traa' — temporal AA without upscaling (same res throughout).
//            Cleaner than TAAU for 1.0 scale and generally less soft,
//            at the cost of no upscale perf win.
//   'off'  — no AA.
//
// Pipelines are lazily built per (aaMode × motionBlur × dof × ssao × ssaoDenoise
// × bloom × anamorphic × lut) combination and cached. Toggling any of these
// off rebuilds (or selects a cached variant) where the corresponding node
// is absent from the graph, so OFF actually reclaims the GPU cost.

import * as THREE from 'three';
import { Node, RenderPipeline, type WebGPURenderer } from 'three/webgpu';
import {
  Fn, uniform, vec2, vec3, vec4, float,
  mix, smoothstep, clamp, luminance, pass, renderOutput, uv, sin, exp, step, dot, length, floor,
  mrt, velocity, output, texture3D, normalView, convertToTexture,
  getViewPosition
} from 'three/tsl';
import { bloom } from 'three/addons/tsl/display/BloomNode.js';
import { taau } from 'three/addons/tsl/display/TAAUNode.js';
import { traa } from 'three/addons/tsl/display/TRAANode.js';
import { sharpen } from 'three/addons/tsl/display/SharpenNode.js';
import { ao } from 'three/addons/tsl/display/GTAONode.js';
import { denoise } from 'three/addons/tsl/display/DenoiseNode.js';
import { fxaaTunable, FXAA_PRESETS, type FXAATunableLive, type FXAAPreset } from './FXAATunable';
import { motionBlur } from './MotionBlur';
import Lut3DNode, { lut3D } from 'three/addons/tsl/display/Lut3DNode.js';
import { dof } from 'three/addons/tsl/display/DepthOfFieldNode.js';
import { anamorphic } from 'three/addons/tsl/display/AnamorphicNode.js';
import { LUTCubeLoader } from 'three/addons/loaders/LUTCubeLoader.js';
import { CONFIG } from '../../config';
import { oceanCellSize, oceanWaveAmp, oceanWaveFalloff, oceanWaveTime } from '../../maps/bundles/sinking-ship/behaviors/oceanWater';

// Single hand-picked LUT shipped with the game. Copy of
// public/luts/Sony/Parasitic-SLog3.cube, selected as the house look.
const PRIMARY_LUT_PATH = '/luts/PrimaryLut.cube';

type AaMode = 'fxaa' | 'taau' | 'traa' | 'off';
type Flags = {
  aaMode: AaMode;
  motionBlur: boolean;
  dof: boolean;
  ssao: boolean;
  ssaoDenoise: boolean;
  bloom: boolean;
  anamorphic: boolean;
  lut: boolean;
};

// Live-tunable shape for the GTAO + Denoise nodes. All knobs are uniforms,
// so collecting refs lets render() push CONFIG edits each frame without
// rebuilding the variant.
type GTAONodeLive = {
  radius: { value: number };
  thickness: { value: number };
  distanceExponent: { value: number };
  distanceFallOff: { value: number };
  scale: { value: number };
  samples: { value: number };
  resolutionScale: number;
};
type DenoiseNodeLive = {
  radius: { value: number };
  lumaPhi: { value: number };
  depthPhi: { value: number };
  normalPhi: { value: number };
};

// Minimum/sane bounds on TAAU knobs so debug sliders can't put the node
// into a state that divides by zero or starves the history. Kept lenient
// — the normal tweak range is far tighter than these, but we want
// `undefined from config` to still produce a valid render.
const TAAU_RES_MIN = 0.25;
const TAAU_RES_MAX = 1.0;

// `taau()` returns a TAAUNode wrapped for TSL use. The runtime instance
// exposes the live tunables we want to edit from the debug panel, but
// its .d.ts doesn't re-export those fields. We cast through this shape.
type TAAUNodeLive = {
  currentFrameWeight: number;
  depthThreshold: number;
  maxVelocityLength: number;
  edgeDepthDiff: number;
};

// Same idea for TRAA — its live fields are narrower (no current-frame
// weight; instead a boolean subpixel correction flag).
type TRAANodeLive = {
  depthThreshold: number;
  maxVelocityLength: number;
  edgeDepthDiff: number;
  useSubpixelCorrection: boolean;
};

/** A minimal 2×2×2 identity LUT used as placeholder before a real one loads. */
function makeIdentityLut(): THREE.Data3DTexture {
  const size = 2;
  const data = new Uint8Array(size * size * size * 4);
  let i = 0;
  for (let z = 0; z < size; z++) {
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        data[i++] = x * 255;
        data[i++] = y * 255;
        data[i++] = z * 255;
        data[i++] = 255;
      }
    }
  }
  const tex = new THREE.Data3DTexture(data, size, size, size);
  tex.type = THREE.UnsignedByteType;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearFilter;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.wrapR = THREE.ClampToEdgeWrapping;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

export class PostPipeline {
  private renderer: WebGPURenderer;
  private scene: THREE.Scene;
  private camera: THREE.Camera;
  private pipelines = new Map<string, RenderPipeline>();

  // LUT plumbing. One shared texture node (swapped in-place when a new LUT
  // is selected) feeds every pipeline variant's lut3D node. Each variant
  // holds its own Lut3DNode, but they all share the same texture object and
  // intensity uniform — only the `size` uniform is per-node.
  private _identityLut = makeIdentityLut();
  private _lutTextureNode = texture3D(this._identityLut);
  private _lut3DNodes: Lut3DNode[] = [];
  private _lutLoader = new LUTCubeLoader();

  readonly uSaturation = uniform(CONFIG.render.saturation);
  readonly uContrast   = uniform(CONFIG.render.contrast);
  readonly uVignette   = uniform(CONFIG.render.vignette);
  readonly uTint       = uniform(new THREE.Color('#fff1d6'));
  readonly uShadowTint = uniform(new THREE.Color('#0f1330'));
  readonly uMotionBlur = uniform(CONFIG.render.motionBlurAmount);
  readonly uLutIntensity = uniform(0);
  // `uDofFocus` is re-computed each frame from camera.position → focus
  // target, so it always tracks the character regardless of camera swing.
  readonly uDofFocus = uniform(10);
  readonly uDofRange = uniform(CONFIG.render.dofFocalLength);
  readonly uDofBokeh = uniform(CONFIG.render.dofBokehScale);
  readonly uAnaIntensity = uniform(0);
  readonly uAnaThreshold = uniform(CONFIG.render.anamorphicThreshold);
  readonly uAnaScale     = uniform(CONFIG.render.anamorphicScale);
  // Flare tint — shared across pipeline variants, written from CONFIG
  // every frame. Each anamorphic node's `colorNode` is swapped to point
  // at this uniform after construction so live edits propagate without
  // pipeline rebuild.
  readonly uAnaColor = uniform(new THREE.Vector3(
    CONFIG.render.anamorphicColorR,
    CONFIG.render.anamorphicColorG,
    CONFIG.render.anamorphicColorB
  ));

  // RCAS sharpen strength (0 = strongest, 2 = none). When
  // `aaTaauSharpen` is false we drive this to 2, so the pass is a
  // visual no-op without needing to rebuild the pipeline.
  readonly uTaauSharpness = uniform(CONFIG.render.aaTaauSharpen
    ? CONFIG.render.aaTaauSharpness : 2);

  // Underwater pass — masked per-pixel by reconstructing each fragment's
  // world Y from scene depth and comparing to the map's `waterY`. Pixels
  // whose visible scene geometry sits below `waterY + maskClearance` get
  // tinted / fogged / refracted; everything above passes through. So
  // when the camera is half-submerged, only the screen region "below the
  // water mesh wave edge" picks up the shader; fully submerged the whole
  // screen is shaded. FxBus drives `uUnderwaterIntensity` (global on/off
  // / ramp) and `uUnderwaterWaterY` (the active map's waterY). Other
  // uniforms mirror CONFIG live.
  readonly uUnderwaterIntensity  = uniform(0);
  readonly uUnderwaterWaterY     = uniform(-1000);
  readonly uUnderwaterClearance  = uniform(CONFIG.render.underwater.maskClearance);
  readonly uUnderwaterTime       = uniform(0);
  readonly uUnderwaterColor      = uniform(new THREE.Color(CONFIG.render.underwater.color));
  readonly uUnderwaterFogDensity = uniform(CONFIG.render.underwater.fogDensity);
  readonly uUnderwaterRefraction = uniform(CONFIG.render.underwater.refraction);
  // Scene-camera matrices for view → world reconstruction. The TSL
  // `cameraProjectionMatrixInverse` / `cameraWorldMatrix` builtins
  // resolve to the ACTIVE camera at shader-build / pipeline time —
  // which inside a post pass is the full-screen ortho cam, not the
  // scene cam we actually want. Holding our own uniforms (refreshed
  // every frame from `this.camera`) sidesteps that. This is the same
  // workaround GTAONode uses for SSAO depth-position reconstruction.
  readonly uSceneProjMatrixInv = uniform(new THREE.Matrix4());
  readonly uSceneCamWorld      = uniform(new THREE.Matrix4());
  readonly uSceneCamPos        = uniform(new THREE.Vector3());

  // Wave parameters — shared uniform instances with oceanWater.ts so
  // the post-pass mask reads the IDENTICAL values the WaterMesh's
  // vertex shader uses. The depth buffer apparently doesn't reflect
  // the vertex displacement reliably (NodeMaterial / TAA interaction),
  // so we bias the mask threshold analytically:
  //   surface = waterY + wave(xz) + clearance
  // Sharing the uniform objects (rather than each side reading
  // performance.now() / CONFIG independently) guarantees byte-
  // identical values regardless of frame ordering or float drift —
  // crucial for keeping the mask boundary glued to the wave
  // silhouette over time.
  readonly uWaveAmp      = oceanWaveAmp;
  readonly uWaveFalloff  = oceanWaveFalloff;
  readonly uWaveTime     = oceanWaveTime;
  // Cell size of the WaterMesh's vertex grid (size / segments). Used to
  // quantize fragment XZ to the same grid the mesh samples and bilerp
  // there — matching the mesh's piecewise-linear surface instead of the
  // smooth analytic, which diverges wherever the mesh undersamples.
  readonly uOceanCellSize = oceanCellSize;

  // World-space point the DOF plane tracks. Written by Renderer every
  // frame via setFocusTarget(). Fallback is a dummy point directly in
  // front of the initial camera so the first couple of frames don't
  // render with an insane focus distance.
  private _focus = new THREE.Vector3(0, 1, 0);
  private _focusSet = false;

  // References to every scene pass + temporal AA node that was built
  // into a pipeline variant. We use these to push live config edits
  // (resolution scale, frame weight, depth threshold…) without
  // rebuilding the pipeline.
  private _taauScenePasses: { setResolutionScale(s: number): unknown }[] = [];
  private _taauNodes: TAAUNodeLive[] = [];
  private _traaNodes: TRAANodeLive[] = [];
  private _fxaaNodes: FXAATunableLive[] = [];
  private _ssaoNodes: GTAONodeLive[] = [];
  private _ssaoDenoiseNodes: DenoiseNodeLive[] = [];

  // AO blend strength uniform. Lives outside the per-variant arrays since
  // the composite uses it as a regular uniform — `mix(1, ao, intensity)`
  // gives the user a 0..1 dial without changing the GTAO node's internals.
  readonly uSsaoIntensity = uniform(CONFIG.render.ssaoIntensity);

  constructor(renderer: WebGPURenderer, scene: THREE.Scene, camera: THREE.Camera, _w: number, _h: number) {
    this.renderer = renderer;
    this.scene = scene;
    this.camera = camera;

    // Load the house LUT so it's live from the first frame. Failures keep
    // the identity placeholder in place (visually a no-op).
    void this.loadPrimaryLut();
  }

  private async loadPrimaryLut(): Promise<void> {
    try {
      const result = await this._lutLoader.loadAsync(PRIMARY_LUT_PATH);
      this._lutTextureNode.value = result.texture3D;
      this._lutTextureNode.needsUpdate = true;
      for (const node of this._lut3DNodes) {
        node.size.value = result.size;
      }
    } catch (err) {
      console.warn(`[PostPipeline] failed to load primary LUT:`, err);
    }
  }

  private keyOf(f: Flags): string {
    // ssaoDenoise only matters when ssao is on but folding it in unconditionally
    // is simpler — at most adds one extra cached variant.
    return `${f.aaMode}-${f.motionBlur ? 1 : 0}${f.dof ? 1 : 0}${f.ssao ? 1 : 0}${f.ssaoDenoise ? 1 : 0}${f.bloom ? 1 : 0}${f.anamorphic ? 1 : 0}${f.lut ? 1 : 0}`;
  }

  private getPipeline(f: Flags): RenderPipeline {
    const k = this.keyOf(f);
    let p = this.pipelines.get(k);
    if (!p) {
      p = this.build(f);
      this.pipelines.set(k, p);
    }
    return p;
  }

  private build(f: Flags): RenderPipeline {
    const scenePass = pass(this.scene, this.camera);

    // Both temporal AA modes need velocity, so enable MRT whenever
    // motion blur OR a temporal AA mode is on. The extra velocity
    // target is essentially free compared to the scene pass itself.
    // SSAO additionally needs view-space normals so GTAO can do its
    // horizon search against real per-pixel normals rather than fall
    // back to depth-derived normals (which soften thin features).
    const isTemporal = f.aaMode === 'taau' || f.aaMode === 'traa';
    const needsVelocity = f.motionBlur || isTemporal;
    const needsNormal = f.ssao;
    if (needsVelocity || needsNormal) {
      const targets: Record<string, unknown> = { output };
      if (needsVelocity) targets.velocity = velocity;
      if (needsNormal) targets.normal = normalView;
      scenePass.setMRT(mrt(targets as Parameters<typeof mrt>[0]));
    }

    // Seed the scene-pass resolution scale from the current config so
    // the first frame after a TAAU build renders at the right input
    // size; render() pushes live edits every frame after that.
    if (f.aaMode === 'taau') {
      const s = clampResScale(CONFIG.render.aaTaauResolutionScale);
      (scenePass as unknown as { setResolutionScale(s: number): unknown })
        .setResolutionScale(s);
      this._taauScenePasses.push(scenePass as unknown as { setResolutionScale(s: number): unknown });
    }

    const rawSceneColor = scenePass.getTextureNode('output');
    let sceneColor: Node<'vec4'> = rawSceneColor as unknown as Node<'vec4'>;

    // SSAO. Inserted at the head of the chain so:
    //   • GTAO sees the un-jittered, un-resolved beauty/normal/depth (it
    //     does its own ray-march in view-space, no need for resolved AA),
    //   • the composite (sceneColor × ao) becomes the input for TAAU/TRAA,
    //     so temporal AA averages the AO's per-frame stochastic noise.
    //
    // Composite is `sceneColor.rgb × mix(1, ao, intensity)` — the intensity
    // uniform lets users dial AO from "off-looking" up to "raw GTAO" without
    // touching the AO node's curve knobs.
    if (f.ssao) {
      const depthTex = scenePass.getTextureNode('depth');
      const normalTex = scenePass.getTextureNode('normal');

      const aoNode = ao(
        depthTex as unknown as Parameters<typeof ao>[0],
        normalTex as unknown as Parameters<typeof ao>[1],
        this.camera
      );
      // Temporal jitter only when there's a TRAA/TAAU history to average
      // it. Without temporal AA, a stable noise pattern is preferable to
      // a flickering one — the bilateral denoise below handles spatial
      // smoothing in that case.
      aoNode.useTemporalFiltering = isTemporal;
      aoNode.radius.value           = CONFIG.render.ssaoRadius;
      aoNode.thickness.value        = CONFIG.render.ssaoThickness;
      aoNode.distanceFallOff.value  = CONFIG.render.ssaoDistanceFallOff;
      aoNode.distanceExponent.value = CONFIG.render.ssaoDistanceExponent;
      aoNode.scale.value            = CONFIG.render.ssaoScale;
      aoNode.samples.value          = CONFIG.render.ssaoSamples;
      aoNode.resolutionScale        = CONFIG.render.ssaoResolutionScale;
      this._ssaoNodes.push(aoNode as unknown as GTAONodeLive);

      // GTAO renders into its own internal RT and exposes it as a
      // sampleable PassTextureNode. That's what we need for both the
      // optional denoise and the composite below.
      const aoTexture = aoNode.getTextureNode() as unknown as Node<'vec4'>;
      let aoResult: Node<'vec4'> = aoTexture;

      if (f.ssaoDenoise) {
        // Bilateral filter (depth + normal aware). 16 rotated taps within
        // `radius` pixels — depth and normal terms keep weights ~0 across
        // silhouettes so AO doesn't bleed past edges.
        const denoiseNode = denoise(
          aoResult as unknown as Parameters<typeof denoise>[0],
          depthTex as unknown as Parameters<typeof denoise>[1],
          normalTex as unknown as Parameters<typeof denoise>[2],
          this.camera
        );
        denoiseNode.radius.value     = CONFIG.render.ssaoDenoiseRadius;
        denoiseNode.lumaPhi.value    = CONFIG.render.ssaoDenoiseLumaPhi;
        denoiseNode.depthPhi.value   = CONFIG.render.ssaoDenoiseDepthPhi;
        denoiseNode.normalPhi.value  = CONFIG.render.ssaoDenoiseNormalPhi;
        this._ssaoDenoiseNodes.push(denoiseNode as unknown as DenoiseNodeLive);
        // DenoiseNode is a TempNode — wrap it so downstream sampling works.
        aoResult = convertToTexture(denoiseNode as unknown as Node<'vec4'>) as unknown as Node<'vec4'>;
      }

      // Composite: AO darkens the scene's RGB. Alpha untouched. The
      // intensity uniform mixes in the AO factor so 0 = visually off,
      // 1 = full AO. convertToTexture renders into an RT that TAAU/TRAA
      // can then sample.
      const aoFactor = mix(float(1), aoResult.r, this.uSsaoIntensity);
      const composited = vec4(sceneColor.rgb.mul(aoFactor), sceneColor.a);
      sceneColor = convertToTexture(composited as unknown as Node<'vec4'>) as unknown as Node<'vec4'>;
    }

    // TAAU runs at the head of the chain so downstream effects (DOF,
    // bloom, grade) see the reconstructed, output-resolution scene.
    // `taau(...)` returns a TAAUNode (a TempNode, no `.sample()`). We
    // feed the chain the PassTextureNode it exposes via getTextureNode()
    // so downstream motion blur's `inputNode.sample(uv)` path works; the
    // PassTextureNode back-references the TAAUNode so its per-frame
    // updateBefore (jitter + resolve + history copy) still runs.
    if (f.aaMode === 'taau') {
      const depthTex = scenePass.getTextureNode('depth');
      const velTex = scenePass.getTextureNode('velocity');
      // Use sceneColor here (not rawSceneColor) so an SSAO composite
      // upstream feeds into TAAU's history accumulation — that's what
      // averages the GTAO per-frame noise to a clean signal.
      const taauNode = taau(
        sceneColor as unknown as Parameters<typeof taau>[0],
        depthTex as unknown as Parameters<typeof taau>[1],
        velTex as unknown as Parameters<typeof taau>[2],
        this.camera
      );
      // Seed live tunables from config; render() refreshes them each frame.
      const live = taauNode as unknown as TAAUNodeLive;
      live.currentFrameWeight   = CONFIG.render.aaTaauCurrentFrameWeight;
      live.depthThreshold       = CONFIG.render.aaTaauDepthThreshold;
      live.maxVelocityLength    = CONFIG.render.aaTaauMaxVelocityLength;
      this._taauNodes.push(live);
      const taauOut = (taauNode as unknown as { getTextureNode(): Node<'vec4'> })
        .getTextureNode();

      // RCAS sharpen pass to recover detail lost to the temporal
      // reconstruction. Sharpness is driven by a uniform so the user
      // can slide from 0 (max sharp) to 2 (none / pass-through) live.
      // We still take the sharpened node's `getTextureNode()` so motion
      // blur's `.sample()` path downstream works.
      const sharpenNode = sharpen(taauOut, this.uTaauSharpness as unknown as number);
      sceneColor = (sharpenNode as unknown as { getTextureNode(): Node<'vec4'> })
        .getTextureNode();
    }

    // TRAA: same family as TAAU but no upscaling. The scene pass
    // always renders at full res; jitter + reproject resolve gives
    // sub-pixel AA. Motion blur still wants a TextureNode input, so
    // we take `.getTextureNode()` off the TRAA result.
    if (f.aaMode === 'traa') {
      const depthTex = scenePass.getTextureNode('depth');
      const velTex = scenePass.getTextureNode('velocity');
      // Use sceneColor (not rawSceneColor) so SSAO's noise is included
      // in TRAA's reprojected history — temporal averaging is what
      // turns the single-frame GTAO output into a stable image.
      const traaNode = traa(
        sceneColor as unknown as Parameters<typeof traa>[0],
        depthTex as unknown as Parameters<typeof traa>[1],
        velTex as unknown as Parameters<typeof traa>[2],
        this.camera
      );
      const live = traaNode as unknown as TRAANodeLive;
      live.depthThreshold        = CONFIG.render.aaTraaDepthThreshold;
      live.maxVelocityLength     = CONFIG.render.aaTraaMaxVelocityLength;
      live.useSubpixelCorrection = CONFIG.render.aaTraaSubpixelCorrection;
      this._traaNodes.push(live);
      sceneColor = (traaNode as unknown as { getTextureNode(): Node<'vec4'> })
        .getTextureNode();
    }

    // Motion blur: uses `inputNode.sample(uv)` internally, so it MUST run
    // directly on a TextureNode (OperatorNode has no `.sample` method).
    // Under TAAU we feed it the resolved output; velocity is still
    // sampled at the scene pass's (possibly lower) input resolution but
    // that's fine — motionBlur works in UV space.
    if (f.motionBlur) {
      const velTex = scenePass.getTextureNode('velocity').mul(this.uMotionBlur);
      sceneColor = motionBlur(sceneColor, velTex);
    }

    // DOF: circle-of-confusion blur driven by viewZ. Runs on the
    // motion-blurred scene (so trailing motion also defocuses when off
    // the focal plane). `dof(...)` calls `convertToTexture()` on its
    // input so feeding an OperatorNode is fine — it'll render to an
    // intermediate RT. ViewZ comes from the scene pass's depth texture.
    // The underwater pass below also reads viewZ for depth-driven fog,
    // so we cache it once (cheap — same node tree).
    const viewZ = scenePass.getViewZNode();
    if (f.dof) {
      sceneColor = dof(sceneColor, viewZ, this.uDofFocus, this.uDofRange, this.uDofBokeh) as unknown as Node<'vec4'>;
    }

    // Bloom + anamorphic are both Flags-gated: when off, the node is
    // absent from the graph entirely (rather than uniform-multiplied
    // to zero) so OFF reclaims the gather/blur GPU cost.
    let bloomed: Node<'vec4'> = sceneColor;
    if (f.bloom) {
      const bloomContribution = bloom(
        sceneColor,
        CONFIG.render.bloomStrength,
        CONFIG.render.bloomRadius,
        CONFIG.render.bloomThreshold
      );
      bloomed = bloomed.add(bloomContribution) as unknown as Node<'vec4'>;
    }

    if (f.anamorphic) {
      // Anamorphic streaks pulled from the same HDR scene. We override the
      // node's `colorNode` with our shared tint uniform so it's live-editable.
      const anaPass = anamorphic(sceneColor, this.uAnaThreshold, this.uAnaScale, 32);
      // `colorNode` is a public field in the addon source but isn't in its
      // public .d.ts; cast through unknown to wire our tint uniform in.
      (anaPass as unknown as { colorNode: unknown }).colorNode = this.uAnaColor;
      const anaContribution = anaPass.mul(this.uAnaIntensity);
      bloomed = bloomed.add(anaContribution) as unknown as Node<'vec4'>;
    }

    const colorGrade = Fn((args: [Node<'vec4'>]) => {
      const [src] = args;
      const col = src.rgb;
      const lum = luminance(col);
      // Lift shadows toward shadow tint; push highlights toward warm tint.
      const shadow = mix(this.uShadowTint, col, smoothstep(0.0, 0.4, lum));
      const hi = mix(col, col.mul(this.uTint), smoothstep(0.55, 1.0, lum));
      const toned = mix(shadow, hi, smoothstep(0.0, 0.55, lum));
      // Saturation + contrast.
      const saturated = mix(vec3(luminance(toned)), toned, this.uSaturation);
      const contrasted = saturated.sub(0.5).mul(this.uContrast).add(0.5);
      // Radial vignette.
      const d = uv().sub(0.5);
      const vig = float(1).sub(d.dot(d).mul(this.uVignette).mul(4));
      const final = contrasted.mul(clamp(vig, 0, 1.2));
      return vec4(final, src.a);
    });

    const graded = colorGrade(bloomed);

    // Underwater pass. Each fragment reconstructs its world Y from the
    // scene-pass depth and applies tint / fog / refraction only when
    // that world Y sits below `waterY + maskClearance`. So:
    //   * Above-water terrain / sky pixels → mask = 0 → pass through.
    //   * WaterMesh fragments (worldY ≈ waterY ± wave amp) → mask = 1.
    //   * Anything else below the surface (sub hull, character bones
    //     swimming) → mask = 1.
    // The visible boundary between "shaded" and "not shaded" is then
    // exactly the silhouette of whatever's been rendered to the depth
    // buffer at waterY — i.e. the wave-displaced WaterMesh edge.
    //
    // `uUnderwaterIntensity` is a global gate (FxBus drives it ~1 while
    // on a wet map, 0 elsewhere — and can ramp for soft on/off if the
    // user wants); the per-pixel mask carries the actual screen-space
    // shape. `convertToTexture(graded)` wraps the prior chain in a
    // sampleable RT so we can re-sample at warped UVs for refraction.
    //
    // We sample the depth texture (NDC z, 0..1 in WebGPU) and feed it
    // through the standard `getViewPosition` helper using OUR uniforms
    // for `projectionMatrixInverse` + `cameraWorldMatrix` — the global
    // TSL builtins resolve to the post-pass full-screen ortho cam in
    // this context, which gave a fixed mid-screen line that didn't move
    // with the player camera. Same workaround GTAONode uses.
    const depthNode = scenePass.getTextureNode('depth');
    const gradedTex = convertToTexture(graded);

    // Analytic wave height at a single world XZ, with the per-vertex fade
    // the WaterMesh uses. Called 4× per pixel from underwaterFn to bilerp
    // at the mesh's vertex grid — see the comment by the bilerp below.
    const waveAtXZ = Fn(([xz]: [Node<'vec2'>]) => {
      const dx = xz.x.sub(this.uSceneCamPos.x);
      const dz = xz.y.sub(this.uSceneCamPos.z);
      const distFromCam = length(vec2(dx, dz));
      const fade = smoothstep(this.uWaveFalloff, float(0), distFromCam);
      const wt = this.uWaveTime;
      const w1 = sin(dot(xz, vec2(0.18, 0.07)).add(wt.mul(0.55)));
      const w2 = sin(dot(xz, vec2(-0.09, 0.21)).add(wt.mul(0.78)));
      const w3 = sin(dot(xz, vec2(0.27, -0.15)).add(wt.mul(0.42)));
      return w1.mul(0.55).add(w2.mul(0.30)).add(w3.mul(0.15))
        .mul(this.uWaveAmp).mul(fade);
    });

    const underwaterFn = Fn((args: [Node<'float'>]) => {
      const [vz] = args;
      const screenUv = uv();
      const t = this.uUnderwaterTime;

      // World-Y reconstruction.
      const depth = depthNode.sample(screenUv).x;
      const viewPos = getViewPosition(screenUv, depth, this.uSceneProjMatrixInv);
      const worldPos = this.uSceneCamWorld.mul(vec4(viewPos.x, viewPos.y, viewPos.z, float(1)));
      const worldY = worldPos.y;

      // Mask: 1 when the visible geometry at this pixel is below the
      // wave-displaced water surface line. We use the IDENTICAL wave
      // formula the WaterMesh's positionNode uses (same constants, same
      // time uniform, same falloff, same camera-distance-based fade), and
      // sample it at the SAME triangle the visible mesh draws — sample at
      // the 3 corner vertices of the fragment's triangle, weight by the
      // barycentric coords. PlaneGeometry triangulates each cell along the
      // b→d diagonal into T1=(a,b,d) and T2=(b,c,d); a continuous eval (or
      // even a 4-corner bilerp) here diverges from the mesh whenever the
      // mesh's piecewise-linear surface differs from the smooth analytic
      // — bilerp adds a saddle term the mesh doesn't have, lifting the
      // post-pass surface above the visible mesh and flipping the mask on
      // for visible water-mesh fragments (those tooth-shaped fog patches
      // peeking above the visible waterline). Triangle interp matches the
      // mesh exactly so the mask stays glued to the visible silhouette.
      //
      // Mesh is rotated -π/2 around X (localY = -worldZ); its plane is
      // centered at world origin with `params.size`/`params.segments`
      // cells. With even segments there's a vertex at world (0, *, 0), so
      // grid alignment needs only the cell size — no offset. (For odd
      // segments this would need an offset uniform too; not the case for
      // any current map.)
      const cell = this.uOceanCellSize;
      const lx = worldPos.x;
      const ly = worldPos.z.negate();
      const ixf = lx.div(cell);
      const jyf = ly.div(cell);
      const ix = floor(ixf);
      const jy = floor(jyf);
      const fx = ixf.sub(ix);
      const fy = jyf.sub(jy);
      const X0 = ix.mul(cell);
      const X1 = X0.add(cell);
      const Z0 = jy.mul(cell).negate();
      const Z1 = Z0.sub(cell);
      // Corner → mesh-vertex mapping. Our (fx, fy) axes run with worldX and
      // negated worldZ, so the cell's lower-Z edge sits at fy=1 (not fy=0):
      //   h_a (lower-left iy, lower X)  = h01    at (fx=0, fy=1)
      //   h_b (upper-left iy, lower X)  = h00    at (fx=0, fy=0)
      //   h_c (upper-right iy, upper X) = h10    at (fx=1, fy=0)
      //   h_d (lower-right iy, upper X) = h11    at (fx=1, fy=1)
      // Diagonal b↔d sits at fx=fy. T1 (a,b,d) covers fy ≥ fx; T2 (b,c,d)
      // covers fy < fx.
      const h00 = waveAtXZ(vec2(X0, Z0));
      const h10 = waveAtXZ(vec2(X1, Z0));
      const h01 = waveAtXZ(vec2(X0, Z1));
      const h11 = waveAtXZ(vec2(X1, Z1));
      const inT1 = step(fx, fy);  // 1 when fy ≥ fx
      // T1 barycentrics: λ_a = fy-fx, λ_b = 1-fy, λ_d = fx
      const heightT1 = h01.mul(fy.sub(fx))
        .add(h00.mul(float(1).sub(fy)))
        .add(h11.mul(fx));
      // T2 barycentrics: λ_b = 1-fx, λ_c = fx-fy, λ_d = fy
      const heightT2 = h00.mul(float(1).sub(fx))
        .add(h10.mul(fx.sub(fy)))
        .add(h11.mul(fy));
      const waveHeight = mix(heightT2, heightT1, inT1);

      // step(edge, x) = 1 if x ≥ edge, so step(worldY, surface) = 1
      // when worldY ≤ surface. Surface = waterY + waveHeight + clearance,
      // i.e. the analytic position of the rendered water surface at this
      // fragment's XZ. Pixels visibly below the wavy mesh edge → masked.
      //
      // Depth clamp: sky / skybox / anything past the far plane lands at
      // viewZ ≈ camera.far. For a camera looking near-horizontally, the
      // reconstructed worldY of those pixels is roughly the camera Y —
      // which trivially passes the surface test when the camera is at
      // or near the water and would smear the underwater shader across
      // the entire above-water horizon. Excluding |viewZ| beyond
      // `farClamp` keeps the mask scoped to genuine in-arena geometry
      // (water mesh, hull, swimmers) and lets the sky pass through
      // un-shaded regardless of camera Y. 200 m is comfortably outside
      // the playable area but well inside the camera's far plane.
      const surface = this.uUnderwaterWaterY.add(waveHeight).add(this.uUnderwaterClearance);
      const inRange = step(vz.abs(), float(200));
      const mask = step(worldY, surface).mul(inRange);

      // Refractive resample: warp uv with a few summed sines so the
      // scene wobbles like it's seen through moving water. Frequencies
      // are picked so X and Y don't share periods (no axis-aligned
      // banding). Multiplied by mask so the warp only applies in the
      // underwater region — above-water pixels read the unwarped scene.
      const refrK = this.uUnderwaterRefraction.mul(mask);
      const warpUv = vec2(
        sin(screenUv.y.mul(38).add(t.mul(2.3))).mul(0.006)
          .add(sin(screenUv.y.mul(11).sub(t.mul(1.1))).mul(0.012))
          .mul(refrK),
        sin(screenUv.x.mul(31).sub(t.mul(1.9))).mul(0.006)
          .add(sin(screenUv.x.mul(8).add(t.mul(0.7))).mul(0.014))
          .mul(refrK)
      );
      const refracted = gradedTex.sample(screenUv.add(warpUv));
      const above = gradedTex.sample(screenUv);

      // Tint + depth-fog the refracted sample. Distant geometry fades
      // into the water "bed" colour exponentially.
      const fogAmount = float(1).sub(exp(vz.abs().negate().mul(this.uUnderwaterFogDensity)));
      const tinted = mix(refracted.rgb, this.uUnderwaterColor, float(0.55));
      const fogged = mix(tinted, this.uUnderwaterColor, clamp(fogAmount, 0, 1));
      const underwaterCol = vec4(fogged, refracted.a);

      // Final composite: mask gates the underwater region in screen
      // space; intensity is a global ramp / disable. Both 0 → above
      // everywhere; mask=1 + intensity=1 → fully underwater for that
      // pixel.
      return mix(above, underwaterCol, mask.mul(this.uUnderwaterIntensity));
    });
    const underwatered = underwaterFn(viewZ);

    const tonemapped = renderOutput(underwatered);
    // FXAA runs after tone mapping so it sees final sRGB colour. Other
    // modes already applied AA at the head of the chain, so the 'taau',
    // 'traa', and 'off' branches just pass through here.
    let aa: Node<'vec4'> = tonemapped as unknown as Node<'vec4'>;
    if (f.aaMode === 'fxaa') {
      const preset = FXAA_PRESETS[CONFIG.render.fxaaQuality as FXAAPreset]
        ?? FXAA_PRESETS.ultra;
      // Seed the tunable FXAA with the current preset values. Live
      // config edits (including preset changes) are pushed onto the
      // uniforms each frame via render() — no rebuild needed.
      const fxaaNode = fxaaTunable(
        tonemapped,
        CONFIG.render.fxaaContrastThreshold || preset.contrast,
        CONFIG.render.fxaaRelativeThreshold || preset.relative,
        CONFIG.render.fxaaSubpixelBlending  || preset.subpixel
      );
      this._fxaaNodes.push(fxaaNode as unknown as FXAATunableLive);
      aa = fxaaNode as unknown as Node<'vec4'>;
    }

    // 3D LUT applied last — operates on display-referred sRGB. Identity
    // placeholder at construction; swapped via setLut() when the user
    // picks one. Size is updated per-LUT (manifest is all 33 today but
    // we don't hardcode the assumption). Flags-gated: when off, the
    // pass is absent from the graph (no 3D-texture sample) and the
    // pipeline outputs `aa` directly.
    let outputNode: Node<'vec4'> = aa;
    if (f.lut) {
      const lutPass = lut3D(aa, this._lutTextureNode, 33, this.uLutIntensity);
      this._lut3DNodes.push(lutPass);
      outputNode = lutPass as unknown as Node<'vec4'>;
    }

    const pipeline = new RenderPipeline(this.renderer);
    pipeline.outputColorTransform = false;
    pipeline.outputNode = outputNode;
    return pipeline;
  }

  /** Override the current saturation uniform. Caller owns the lerp / decay;
   *  this is a plain write so KO replay can ramp it per frame without the
   *  pipeline holding hidden state. */
  setSaturation(v: number): void {
    this.uSaturation.value = v;
  }

  /** World-space point the DOF focus plane should sit on. Renderer calls
   *  this each frame with the focused (or spectated) character's position
   *  so they stay sharp even as the camera orbits around them. */
  setFocusTarget(x: number, y: number, z: number): void {
    this._focus.set(x, y, z);
    this._focusSet = true;
  }

  /** FxBus calls this every frame with the current underwater state.
   *  `intensity` 0..1 globally gates / ramps the whole effect (set 0 on
   *  dry maps). `waterY` is the active map's water surface Y in world
   *  space — used by the per-pixel mask to decide which fragments sit
   *  below the surface. */
  setUnderwater(intensity: number, waterY: number): void {
    this.uUnderwaterIntensity.value = intensity;
    this.uUnderwaterWaterY.value = waterY;
  }

  render(dt: number): void {
    this.uMotionBlur.value = CONFIG.render.motionBlurAmount;
    // LUT is Flags-gated: when off, the lut3D pass is absent. The
    // intensity uniform only controls blend strength when the pass is
    // built into the active variant.
    this.uLutIntensity.value = CONFIG.render.lutIntensity;

    // Refresh the scene-camera matrices the underwater pass uses for
    // depth → world Y reconstruction. Camera matrices auto-update when
    // the renderer renders the scene; we copy them here so the post
    // pass sees this frame's values rather than last frame's. The TSL
    // global `cameraProjectionMatrixInverse` doesn't help us here
    // because it resolves to the post pass's ortho cam, not this one.
    const cam = this.camera as THREE.PerspectiveCamera;
    cam.updateMatrixWorld();
    this.uSceneProjMatrixInv.value.copy(cam.projectionMatrixInverse);
    this.uSceneCamWorld.value.copy(cam.matrixWorld);
    this.uSceneCamPos.value.setFromMatrixPosition(cam.matrixWorld);

    // Underwater pass live tweaks. `time` accumulates so the refraction
    // warp animates across frames; the rest are plain CONFIG mirrors.
    // Intensity + waterY are written by FxBus via setUnderwater().
    this.uUnderwaterTime.value += dt;
    this.uUnderwaterColor.value.set(CONFIG.render.underwater.color);
    this.uUnderwaterFogDensity.value = CONFIG.render.underwater.fogDensity;
    this.uUnderwaterRefraction.value = CONFIG.render.underwater.refraction;
    this.uUnderwaterClearance.value  = CONFIG.render.underwater.maskClearance;
    // (Wave amp / falloff / time uniforms are SHARED with oceanWater.ts
    // — its stepRender writes them once per frame and the post pass
    // reads the same uniform instances. Don't write them here or
    // they'd race / drift relative to the visible mesh.)

    // Auto-focus: distance from camera to the tracked character (plus a
    // user-tunable offset). Camera yaw/pitch don't affect the distance,
    // so the character stays sharp at any camera angle. Clamp to a small
    // positive floor since a zero/negative focus distance makes DOF blow up.
    if (this._focusSet) {
      const d = this.camera.position.distanceTo(this._focus);
      this.uDofFocus.value = Math.max(0.1, d + CONFIG.render.dofFocusOffset);
    }
    this.uDofRange.value = CONFIG.render.dofFocalLength;
    this.uDofBokeh.value = CONFIG.render.dofBokehScale;
    // Anamorphic is Flags-gated: when off, the gather pass is absent.
    // The intensity uniform only controls strength inside the gated
    // variant.
    this.uAnaIntensity.value = CONFIG.render.anamorphicIntensity;
    this.uAnaThreshold.value = CONFIG.render.anamorphicThreshold;
    this.uAnaScale.value     = CONFIG.render.anamorphicScale;
    this.uAnaColor.value.set(
      CONFIG.render.anamorphicColorR,
      CONFIG.render.anamorphicColorG,
      CONFIG.render.anamorphicColorB
    );

    // Push TAAU live tweaks. Cheap no-op when no TAAU pipeline has been
    // built (the arrays stay empty on the FXAA/off paths).
    if (this._taauScenePasses.length > 0) {
      const s = clampResScale(CONFIG.render.aaTaauResolutionScale);
      for (const p of this._taauScenePasses) p.setResolutionScale(s);
    }
    if (this._taauNodes.length > 0) {
      const w = CONFIG.render.aaTaauCurrentFrameWeight;
      const dt = CONFIG.render.aaTaauDepthThreshold;
      const mv = CONFIG.render.aaTaauMaxVelocityLength;
      for (const n of this._taauNodes) {
        n.currentFrameWeight = w;
        n.depthThreshold = dt;
        n.maxVelocityLength = mv;
      }
    }
    // RCAS sharpness uniform: 2 = pass-through, so toggling
    // `aaTaauSharpen` off costs one quad draw but produces no visible
    // effect (no variant rebuild).
    this.uTaauSharpness.value = CONFIG.render.aaTaauSharpen
      ? CONFIG.render.aaTaauSharpness : 2;

    // TRAA live tweaks. Same pattern as TAAU — empty array means no
    // TRAA pipeline has ever been built, so the loop is zero cost.
    if (this._traaNodes.length > 0) {
      const dt = CONFIG.render.aaTraaDepthThreshold;
      const mv = CONFIG.render.aaTraaMaxVelocityLength;
      const sp = CONFIG.render.aaTraaSubpixelCorrection;
      for (const n of this._traaNodes) {
        n.depthThreshold = dt;
        n.maxVelocityLength = mv;
        n.useSubpixelCorrection = sp;
      }
    }

    // FXAA live tweaks. Preset picks default numbers; if the user has
    // edited the raw sliders we push those instead. Presets are just
    // shortcuts that write the three raw fields.
    if (this._fxaaNodes.length > 0) {
      const c = CONFIG.render.fxaaContrastThreshold;
      const r = CONFIG.render.fxaaRelativeThreshold;
      const s = CONFIG.render.fxaaSubpixelBlending;
      for (const n of this._fxaaNodes) {
        n.uContrast.value = c;
        n.uRelative.value = r;
        n.uSubpixel.value = s;
      }
    }

    // SSAO live tweaks. Empty arrays when no SSAO variant has been
    // built, so the loops are zero-cost on the off path.
    this.uSsaoIntensity.value = CONFIG.render.ssaoIntensity;
    if (this._ssaoNodes.length > 0) {
      const r  = CONFIG.render.ssaoRadius;
      const t  = CONFIG.render.ssaoThickness;
      const df = CONFIG.render.ssaoDistanceFallOff;
      const de = CONFIG.render.ssaoDistanceExponent;
      const sc = CONFIG.render.ssaoScale;
      const sa = CONFIG.render.ssaoSamples;
      const rs = CONFIG.render.ssaoResolutionScale;
      for (const n of this._ssaoNodes) {
        n.radius.value           = r;
        n.thickness.value        = t;
        n.distanceFallOff.value  = df;
        n.distanceExponent.value = de;
        n.scale.value            = sc;
        n.samples.value          = sa;
        n.resolutionScale        = rs;
      }
    }
    if (this._ssaoDenoiseNodes.length > 0) {
      const r  = CONFIG.render.ssaoDenoiseRadius;
      const lp = CONFIG.render.ssaoDenoiseLumaPhi;
      const dp = CONFIG.render.ssaoDenoiseDepthPhi;
      const np = CONFIG.render.ssaoDenoiseNormalPhi;
      for (const n of this._ssaoDenoiseNodes) {
        n.radius.value     = r;
        n.lumaPhi.value    = lp;
        n.depthPhi.value   = dp;
        n.normalPhi.value  = np;
      }
    }

    const pipeline = this.getPipeline({
      aaMode: CONFIG.render.aaMode,
      motionBlur: CONFIG.render.motionBlur,
      dof: CONFIG.render.dof,
      ssao: CONFIG.render.ssao,
      ssaoDenoise: CONFIG.render.ssaoDenoise,
      bloom: CONFIG.render.bloom,
      anamorphic: CONFIG.render.anamorphic,
      lut: CONFIG.render.lut
    });
    pipeline.render();
  }

  setSize(_w: number, _h: number): void {
    // RenderPipeline auto-adapts to renderer size; nothing to do.
  }
}

function clampResScale(s: number): number {
  if (!Number.isFinite(s)) return 1;
  if (s < TAAU_RES_MIN) return TAAU_RES_MIN;
  if (s > TAAU_RES_MAX) return TAAU_RES_MAX;
  return s;
}
